import { isEmpty } from 'lodash'

// =============================================================================
// Pure helpers for the call-flow canvas. Kept separate from the
// React components so they can be unit-tested without rendering. All visual
// metadata (icons, colors, copy) lives here so the canvas itself stays thin.
// =============================================================================

// ---------------------------------------------------------------------------
// Node types
// ---------------------------------------------------------------------------

export const NODE_TYPE_LABEL = {
  entry: 'Call Start',
  greeting: 'Greeting',
  prompt: 'Prompt',
  subagent: 'Subagent',
  decision: 'Decision',
  tool: 'Tool',
  exit: 'Call End',
}

export const NODE_TYPE_DESCRIPTION = {
  entry: 'Where the conversation begins — the first node the agent hits.',
  greeting: 'The opening line the agent says out loud when the call starts.',
  prompt: 'Internal instructions the AI follows — not spoken to the caller word-for-word.',
  subagent:
    'A specialized AI that takes over for a part of the call. The one with a blue glow and "Entry point" pill is the entry agent.',
  decision: 'A branching point — the agent picks one path based on what happened.',
  tool: 'An action the agent takes outside the conversation (e.g., send an SMS).',
  exit: 'Where the flow ends — transfer, hangup, or handoff.',
}

export const NODE_TYPE_ORDER = [
  'entry',
  'greeting',
  'prompt',
  'subagent',
  'decision',
  'tool',
  'exit',
]

// Inner SVG markup for each node type's icon. Components wrap this string in
// a 24×24 outer <svg> with currentColor stroke + standard linecap defaults;
// individual shapes are free to override stroke / stroke-width / fill in
// their own attributes.
//
// `subagent`, `entry`, and `exit` use the exact paths from the design mock
// (face-in-rounded-square, phone-incoming, phone-off respectively). The
// remaining types use the in-house glyphs that already shipped — they read
// fine against the design even though they're not exact 1:1 matches.
export const NODE_ICON_PATHS = {
  entry:
    '<path d="M7.91667 7.33334H10.25L11.4167 10.25L9.95833 11.125C10.5831 12.3917 11.6083 13.417 12.875 14.0417L13.75 12.5833L16.6667 13.75V16.0833C16.6667 16.3928 16.5438 16.6895 16.325 16.9083C16.1062 17.1271 15.8094 17.25 15.5 17.25C13.2246 17.1117 11.0784 16.1455 9.46648 14.5335C7.85454 12.9216 6.88828 10.7754 6.75 8.50001C6.75 8.19059 6.87292 7.89384 7.09171 7.67505C7.3105 7.45626 7.60725 7.33334 7.91667 7.33334Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>',
  // Speech bubble with three dots — semantically "the agent says hello".
  // Picked to look nothing like the phone-handset entry icon so the two
  // are immediately distinguishable in the canvas.
  greeting:
    '<path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5V15a1.5 1.5 0 0 1-1.5 1.5H10l-4 4v-4H5.5A1.5 1.5 0 0 1 4 15V5.5z"/><circle cx="8.5" cy="10.25" r="0.8" fill="currentColor" stroke="none"/><circle cx="12" cy="10.25" r="0.8" fill="currentColor" stroke="none"/><circle cx="15.5" cy="10.25" r="0.8" fill="currentColor" stroke="none"/>',
  prompt:
    '<path d="M4 5h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-4 4v-4H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"/><line x1="6" y1="9" x2="14" y2="9"/><line x1="6" y1="12" x2="12" y2="12"/>',
  subagent:
    '<rect x="6.625" y="5.625" width="10.75" height="6.75" rx="3.375" stroke="currentColor" stroke-width="1.25" fill="none"/><rect x="6.625" y="15.625" width="10.75" height="2.75" rx="1.375" stroke="currentColor" stroke-width="1.25" fill="none"/><ellipse cx="10.1393" cy="9.09375" rx="1.13932" ry="1.09375" fill="currentColor" stroke="none"/><ellipse cx="13.6081" cy="9.09375" rx="1.13932" ry="1.09375" fill="currentColor" stroke="none"/><rect x="11.4167" y="12.84" width="1.16667" height="2.79999" fill="currentColor" stroke="none"/>',
  decision:
    '<polygon points="9 3 17 11 9 19 1 11 9 3"/><path d="M17 11 L22 5"/><polyline points="19 5 22 5 22 8"/><path d="M17 11 L22 17"/><polyline points="22 14 22 17 19 17"/>',
  tool: '<path d="M11.5 6.8a4.3 4.3 0 0 0-4 5.8L3 17.1a1.7 1.7 0 0 0 2.4 2.4l4.5-4.5a4.3 4.3 0 1 0 1.6-8.2z"/><circle cx="18.5" cy="6.5" r="2.4"/>',
  exit: '<path d="M6.75 17.25L17.25 6.75" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.40142 13.2606C7.42343 11.8559 6.85199 10.2086 6.75 8.50001C6.75 8.19059 6.87292 7.89384 7.09171 7.67505C7.3105 7.45626 7.60725 7.33334 7.91667 7.33334H10.25L11.4167 10.25L9.95833 11.125C10.0213 11.2533 10.0884 11.3788 10.1596 11.5013M11.181 12.8295C11.6728 13.3195 12.2438 13.7302 12.875 14.0417L13.75 12.5833L16.6667 13.75V16.0833C16.6667 16.3928 16.5438 16.6895 16.325 16.9083C16.1062 17.1271 15.8094 17.25 15.5 17.25C13.2239 17.1138 11.0769 16.1472 9.466 14.5334" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" fill="none"/>',
}

// ---------------------------------------------------------------------------
// Edge categories
// ---------------------------------------------------------------------------

const BLURPLE_COLOR_VAR = '--color-brand'
const EDGE_SUBAGENT_TRANSFER = 'subagent-transfer'

export const EDGE_CATEGORIES = {
  'subagent-transfer': {
    label: 'Transfer to sub-agent',
    colorVar: BLURPLE_COLOR_VAR,
    description: 'Passes the conversation to another AI agent that specializes in a topic.',
  },
  'tool-call': {
    label: 'Tool call',
    colorVar: '--color-neutral',
    description: 'Runs an external action like sending an SMS or looking up a record.',
  },
  success: {
    label: 'Success',
    colorVar: '--color-success',
    description: 'Fires when a step completes successfully.',
  },
  failure: {
    label: 'Failure',
    colorVar: '--color-warning',
    description: 'Fires when a step fails or retries run out.',
  },
  'escalate-human': {
    label: 'Escalate to human',
    colorVar: '--color-caution',
    description: 'Hands the call to a live agent.',
  },
  'end-call': {
    label: 'End call',
    colorVar: '--color-critical',
    description: 'Ends the conversation with the caller.',
  },
  'routing-decision': {
    label: 'Routing decision',
    colorVar: BLURPLE_COLOR_VAR,
    description: 'Picks one path out of several based on what the caller said.',
  },
  default: {
    label: 'Default',
    colorVar: '--color-neutral',
    description: 'Moves to the next step automatically, no special condition.',
  },
  'peer-handoff': {
    label: 'Peer handoff',
    colorVar: BLURPLE_COLOR_VAR,
    description:
      'Optional pivot to another sub-agent when the caller changes topic mid-conversation.',
  },
}

export const EDGE_CATEGORY_ORDER = [
  EDGE_SUBAGENT_TRANSFER,
  'tool-call',
  'success',
  'failure',
  'escalate-human',
  'end-call',
  'routing-decision',
  'default',
]

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

// Default node size used by the auto-layout (column / row spacing budget).
// Represents the maximum rendered box — sub-agent cards with a full chip
// row can be ~200px tall. computeWorldSize / computeFitTransform use this
// to keep the canvas viewport tall enough for the tallest node.
export const NODE_BOX = { width: 260, height: 200 }

// Per-type rendered box dimensions used by edge-anchor math. Short utility
// nodes (entry / greeting / exit / decision / tool) don't carry a chip row,
// so their actual visible height is ~96–120px. Using the 200px layout
// budget for the anchor y-midpoint would put the bezier endpoint below the
// visible card — which is exactly the "edges land in the bottom corner"
// bug surfaced in the design review. Sub-agent / prompt cards CAN have a
// chip row, so they still use the full 200px estimate.
export const NODE_BOX_BY_TYPE = {
  entry: { width: 260, height: 96 },
  greeting: { width: 260, height: 120 },
  prompt: { width: 260, height: 200 },
  subagent: { width: 260, height: 220 },
  decision: { width: 260, height: 96 },
  tool: { width: 260, height: 96 },
  exit: { width: 260, height: 96 },
}

export const getNodeBox = (type) => NODE_BOX_BY_TYPE[type] || NODE_BOX

// Returns {x, y} for a node, falling back to a deterministic position so the
// canvas never ends up with stacked-on-origin nodes when the BE doesn't ship
// coordinates. The fallback is index-based — fine for 1–2-node graphs.
export const getNodePosition = (node, fallbackIndex = 0) => {
  if (typeof node?.x === 'number' && typeof node?.y === 'number') {
    return { x: node.x, y: node.y }
  }
  return { x: 60 + fallbackIndex * 280, y: 220 }
}

// Layered top-down BFS layout for a call-flow graph. Used when the agent
// doesn't ship explicit x/y coordinates (real BE config from YAML doesn't).
// Strategy:
//   1. Find roots (in-degree 0, or any `entry` node).
//   2. BFS, assigning each node a depth (column) = longest path from a root.
//   3. Disconnected nodes get parked in depth 0.
//   4. Each depth lays out as a vertical column; depths advance left → right.
// Returns a `{ [nodeId]: {x, y} }` map.
export const computeLayeredLayout = (
  nodes = [],
  edges = [],
  // Spacing must accommodate the redesigned 260px-wide / ~200px-tall node.
  // Previous defaults (340 / 150) were tuned for the smaller card and let
  // tall same-column nodes overlap visually after the chip row landed.
  { columnSpacing = 400, rowSpacing = 240, startX = 60, startY = 80 } = {}
) => {
  if (isEmpty(nodes)) {
    return {}
  }
  const ids = nodes.map((node) => node.id)
  const outgoing = {}
  const indegree = {}
  ids.forEach((id) => {
    outgoing[id] = []
    indegree[id] = 0
  })
  edges.forEach((edge) => {
    if (outgoing[edge.from] && indegree[edge.to] !== undefined && edge.from !== edge.to) {
      outgoing[edge.from].push(edge.to)
      indegree[edge.to] += 1
    }
  })

  const depth = {}
  // Seed BFS from explicit entry nodes (preferred) and any other in-degree 0
  // nodes (covers graphs where the entry doesn't carry the 'entry' type).
  const roots = nodes
    .filter((node) => node.type === 'entry' || indegree[node.id] === 0)
    .map((node) => node.id)
  const seeds = !isEmpty(roots) ? roots : [ids[0]]
  const queue = []
  seeds.forEach((id) => {
    depth[id] = 0
    queue.push(id)
  })

  while (!isEmpty(queue)) {
    const current = queue.shift()
    outgoing[current].forEach((next) => {
      const candidate = depth[current] + 1
      if (depth[next] === undefined || depth[next] < candidate) {
        depth[next] = candidate
        queue.push(next)
      }
    })
  }

  // Tool nodes that nothing routes to via the BFS still need a slot — park
  // them one column past their first caller to keep them visually attached.
  ids.forEach((id) => {
    if (depth[id] !== undefined) {
      return
    }
    const caller = edges.find((edge) => edge.to === id && depth[edge.from] !== undefined)
    depth[id] = caller ? depth[caller.from] + 1 : 0
  })

  const columns = {}
  // Preserve the declared node order within each column — gives a stable,
  // predictable layout instead of one that flips on every render.
  nodes.forEach((node) => {
    const column = depth[node.id]
    if (!columns[column]) {
      columns[column] = []
    }
    columns[column].push(node.id)
  })

  const positions = {}
  Object.keys(columns).forEach((columnKey) => {
    const column = Number(columnKey)
    const nodeIds = columns[columnKey]
    nodeIds.forEach((id, indexInColumn) => {
      positions[id] = {
        x: startX + column * columnSpacing,
        y: startY + indexInColumn * rowSpacing,
      }
    })
  })
  return positions
}

// Drop tool nodes (and any edges touching them) from an agent for rendering —
// tools are surfaced as chips inside the agents/prompts that call them, so
// showing them as graph nodes would add arrows to nothing. Returns the agent
// unchanged when there are no tools (or it's not a node-bearing object); the
// Redux copy is never mutated — drawer/handoff/variable/tool selectors still
// see the full graph.
export const stripToolNodes = (agent) => {
  if (!agent || !Array.isArray(agent.nodes)) {
    return agent
  }
  const toolNodeIds = new Set(
    agent.nodes.filter((node) => node.type === 'tool').map((node) => node.id)
  )
  if (toolNodeIds.size === 0) {
    return agent
  }
  return {
    ...agent,
    nodes: agent.nodes.filter((node) => !toolNodeIds.has(node.id)),
    edges: Array.isArray(agent.edges)
      ? agent.edges.filter((edge) => !toolNodeIds.has(edge.from) && !toolNodeIds.has(edge.to))
      : agent.edges,
  }
}

// Resolves positions for every node in an agent. If every node ships explicit
// x/y, those are used as-is (hand-tuned mock fixtures stay pristine).
// Otherwise the graph is laid out with `computeLayeredLayout` — mixing
// explicit + computed positions produces visually jarring results, so it's
// all-or-nothing per agent.
export const computeAgentLayout = (agent, options) => {
  const nodes = agent?.nodes
  if (isEmpty(nodes)) {
    return {}
  }
  const allHaveCoordinates = nodes.every(
    (node) => typeof node?.x === 'number' && typeof node?.y === 'number'
  )
  if (allHaveCoordinates) {
    const explicit = {}
    nodes.forEach((node) => {
      explicit[node.id] = { x: node.x, y: node.y }
    })
    return explicit
  }
  return computeLayeredLayout(nodes, agent?.edges || [], options)
}

// Compute the world-space dimensions needed to contain every node, with a
// margin so badges/edges drawn beyond the rightmost / bottommost node aren't
// cropped by the SVG viewport. Used to size the absolutely-positioned
// canvas-inner so its layout box matches the visible content extent.
export const computeWorldSize = (positions, margin = 120) => {
  const entries = Object.values(positions || {})
  if (isEmpty(entries)) {
    return { width: 0, height: 0 }
  }
  const maxX = Math.max(...entries.map((p) => p.x + NODE_BOX.width))
  const maxY = Math.max(...entries.map((p) => p.y + NODE_BOX.height))
  return {
    width: Math.max(0, maxX) + margin,
    height: Math.max(0, maxY) + margin,
  }
}

// Compute zoom + pan so that every node fits inside the canvas viewport with
// a small padding margin. Returns null when there's not enough info to fit
// (caller should leave the current view alone). Used on initial agent load
// AND for the explicit "Fit to view" button so the user always has a way back
// to a sensible default after panning/zooming far off-screen.
export const computeFitTransform = (positions, canvasWidth, canvasHeight, options = {}) => {
  const padding = options.padding ?? 48
  const minZoom = options.minZoom ?? 0.35
  const maxZoom = options.maxZoom ?? 1
  // Vertical anchor strategies:
  //   'auto'   — center when scaled content fits comfortably inside the
  //              canvas (≤ centerThreshold of available height); top-anchor
  //              otherwise. Default. Matches Figma/n8n intuition: small
  //              graphs (Simple Greeter — 3 nodes in a row) sit centered;
  //              large graphs (Westwood POC2 — 13 nodes) hug the top so
  //              the entry point is in view without panning.
  //   'top'    — always hug the top edge with `padding` margin.
  //   'center' — always center vertically.
  const verticalAnchor = options.verticalAnchor ?? 'auto'
  const centerThreshold = options.centerThreshold ?? 0.65
  const entries = Object.values(positions || {})
  if (isEmpty(entries) || !canvasWidth || !canvasHeight) {
    return null
  }
  const minX = Math.min(...entries.map((p) => p.x))
  const minY = Math.min(...entries.map((p) => p.y))
  const maxX = Math.max(...entries.map((p) => p.x + NODE_BOX.width))
  const maxY = Math.max(...entries.map((p) => p.y + NODE_BOX.height))
  const contentWidth = maxX - minX
  const contentHeight = maxY - minY
  const availableWidth = Math.max(1, canvasWidth - padding * 2)
  const availableHeight = Math.max(1, canvasHeight - padding * 2)
  const zoomX = availableWidth / contentWidth
  const zoomY = availableHeight / contentHeight
  const fitZoom = Math.min(maxZoom, Math.max(minZoom, Math.min(zoomX, zoomY)))
  // Horizontal: always center.
  const contentCenterX = (minX + contentWidth / 2) * fitZoom
  const panX = canvasWidth / 2 - contentCenterX
  // Vertical: pick anchor strategy.
  const scaledContentHeight = contentHeight * fitZoom
  const useCenter =
    verticalAnchor === 'center' ||
    (verticalAnchor === 'auto' && scaledContentHeight <= availableHeight * centerThreshold)
  let panY
  if (useCenter) {
    const contentCenterY = (minY + contentHeight / 2) * fitZoom
    panY = canvasHeight / 2 - contentCenterY
  } else {
    panY = padding - minY * fitZoom
  }
  return { zoom: fitZoom, pan: { x: panX, y: panY } }
}

// ---------------------------------------------------------------------------
// Peer-handoff overlay
// ---------------------------------------------------------------------------

// Returns the peer node ids that should render as a dashed overlay when this
// sub-agent is selected. Excludes peers already connected by a primary edge
// in either direction (avoid drawing a redundant dashed line over a solid one).
export const getOverlayPeerIds = (node, agent) => {
  if (!node || node.type !== 'subagent' || !Array.isArray(node.peerHandoffs)) {
    return []
  }
  if (!agent || !Array.isArray(agent.edges)) {
    return node.peerHandoffs
  }
  return node.peerHandoffs.filter((peerId) => {
    return !agent.edges.some(
      (edge) =>
        (edge.from === node.id && edge.to === peerId) ||
        (edge.from === peerId && edge.to === node.id)
    )
  })
}

// Builds the trigger description + tool name for a peer-handoff edge. Used by
// the popover when a synthesized peer edge is opened.
export const getHandoffDetails = (sourceNode, targetNode, agent) => {
  if (!sourceNode || !targetNode) {
    return { tool: '', trigger: '' }
  }
  const peerName = targetNode.config?.name || targetNode.id
  const fwd = agent?.edges?.find(
    (edge) =>
      edge.from === sourceNode.id &&
      edge.to === targetNode.id &&
      edge.edgeCategory === EDGE_SUBAGENT_TRANSFER
  )
  if (fwd) {
    const tool = sourceNode.isMaster ? `handoff_to_${peerName}` : `request_handoff("${peerName}")`
    return { tool, trigger: fwd.description || '' }
  }
  const back = agent?.edges?.find(
    (edge) =>
      edge.from === targetNode.id &&
      edge.to === sourceNode.id &&
      edge.bidirectional &&
      edge.edgeCategory === EDGE_SUBAGENT_TRANSFER
  )
  if (back?.returnDescription) {
    return { tool: `request_handoff("${peerName}")`, trigger: back.returnDescription }
  }
  return {
    tool: `request_handoff("${peerName}")`,
    trigger: `When the caller pivots to ${(targetNode.title || peerName).toLowerCase()}'s topic.`,
  }
}

// ---------------------------------------------------------------------------
// Edge mechanism — the "How it works" copy on the popover
// ---------------------------------------------------------------------------

export const getEdgeMechanism = (edge, category, sourceNode, targetNode) => {
  const src = sourceNode?.title || edge?.from || ''
  const tgt = targetNode?.title || edge?.to || ''
  switch (category) {
    case EDGE_SUBAGENT_TRANSFER: {
      const toolName = sourceNode?.isMaster
        ? `handoff_to_${edge?.to}`
        : `request_handoff("${edge?.to}")`
      return `\`${src}\` calls the \`${toolName}\` tool. The runtime swaps the active sub-agent to \`${tgt}\`, which takes over on its own prompt and tool set.`
    }
    case 'tool-call': {
      const toolName = targetNode?.config?.name || targetNode?.title || edge?.to
      return `\`${src}\` invokes the \`${toolName}\` tool. Execution pauses until the tool returns.`
    }
    case 'success':
      return `The preceding step (\`${src}\`) reported success. The runtime follows this edge to \`${tgt}\`.`
    case 'failure':
      return `\`${src}\` failed or exhausted its retries. The runtime follows this edge to \`${tgt}\` instead of the success path.`
    case 'escalate-human':
      return `The agent calls \`transfer_to_live_agent\`, bridging the caller to a human.`
    case 'end-call':
      return `The agent calls \`end_call\`, terminating the call on the telephony side.`
    case 'routing-decision':
      return `Decision node \`${src}\` evaluates caller state and picks this branch.`
    case 'peer-handoff': {
      const peerName = targetNode?.config?.name || targetNode?.id || edge?.to
      const toolName = sourceNode?.isMaster
        ? `handoff_to_${peerName}`
        : `request_handoff("${peerName}")`
      return `\`${src}\` calls \`${toolName}\`. Peer pivot — the two sub-agents are equals.`
    }
    default:
      return `Execution advances from \`${src}\` to \`${tgt}\` automatically — no tool call or condition in between.`
  }
}

// Render a backticked-string as an array of React-compatible parts:
// regular text + <code>…</code> for backtick-wrapped fragments.
export const splitMechanismForRender = (text) => {
  if (!text) {
    return []
  }
  const parts = []
  const regex = /`([^`]+)`/g
  let lastIndex = 0
  let match = regex.exec(text)
  while (match) {
    if (match.index > lastIndex) {
      parts.push({ kind: 'text', value: text.slice(lastIndex, match.index) })
    }
    parts.push({ kind: 'code', value: match[1] })
    lastIndex = match.index + match[0].length
    match = regex.exec(text)
  }
  if (lastIndex < text.length) {
    parts.push({ kind: 'text', value: text.slice(lastIndex) })
  }
  return parts
}

// ---------------------------------------------------------------------------
// LocalStorage persistence
// ---------------------------------------------------------------------------

export const STORAGE_KEYS = {
  legendCollapsed: 'voiceAgents.legendCollapsed',
  hintDismissed: 'voiceAgents.hintDismissed',
}

export const readPersistedFlag = (key) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false
  }
  try {
    return window.localStorage.getItem(key) === 'true'
  } catch (storageError) {
    return false
  }
}

export const writePersistedFlag = (key, value) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  try {
    window.localStorage.setItem(key, value ? 'true' : 'false')
  } catch (storageError) {
    // ignore quota / privacy errors
  }
}
